import { SvgIcon, Tooltip } from '@mui/material';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { paths } from 'src/paths';
import {
  hasLaborManagementAccess,
  hasLaborValidationViewAccess,
  Permissions,
  useScopeChecker,
} from 'src/rbac';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { SQ_60_REARCHIVE_NAVIGATION } from 'src/constants/feature-treatments';
import { mdsDashboardNewTreatment, npcLogTreatment } from 'src/config';
import { getRearchiveUrl } from 'src/utils/rearchiveUrl';

import { useSelector } from 'react-redux';
import {
  DailySessionNotesIcon,
  LaborManagementIcon,
  MonthEndAlertIcon,
  MyDashboardHomeIcon,
  MyDepartmentDashboardIcon,
  PatientIcon,
  ReportsIcon,
  SchedulingIcon,
} from './SideBarIcon/sideBarIcon';

export interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
}

export interface Section {
  items: Item[];
  subheader?: string;
}

export const useSections = () => {
  const { t } = useTranslation();
  const has = useScopeChecker();
  const [isRearchiveNavigationEnabled] = useFeatureToggleWhenReady(
    SQ_60_REARCHIVE_NAVIGATION,
    'ENTERPRISE'
  );
  const [isMdsDashboardEnabled] = useFeatureToggleWhenReady(mdsDashboardNewTreatment, 'FACILITY');

  const isLaborValidationEnabled = useSelector(
    (state: any) => state?.getLaborValidationToggleReducer?.response?.active
  );
  const [isNpcLogEnabled] = useFeatureToggleWhenReady(
    npcLogTreatment,
    'FACILITY'
  );

  const canAccessLaborManagement = hasLaborManagementAccess(has);
  const canViewLaborValidation = hasLaborValidationViewAccess(has);

  // const thePayComeDetailsReducer = useSelector(
  //   (state: any) => state.thePayComeDetailsReducer?.response?.data
  // );

  return useMemo(() => {
    const sidebarList = [
      {
        subheader:
          has(Permissions.READ_BOARD_SELF) ||
          has(Permissions.READ_BOARD) ||
          has(Permissions.READ_MDS_DASHBOARD) ||
          has(Permissions.READ_MDS_COORDINATOR_BOARD)
            ? t('Dashboards')
            : '',
        items: [
          {
            title: t('My Dashboard'),
            path: paths.dashboard,
            icon: (
              // <SvgIcon fontSize="medium">
              <MyDashboardHomeIcon />
              // </SvgIcon>
            ),
          },
          {
            title: t('Physician Dashboard'),
            path: paths.physicianDashboard,
            icon: (
              <SvgIcon fontSize="medium">
                <MyDepartmentDashboardIcon />
              </SvgIcon>
            ),
          },
          {
            title: t('Compliance & Alerts'),
            path: paths.departmentComplianceAlerts,
            icon: (
              <SvgIcon fontSize="medium">
                <Tooltip title={'Compliance & Alerts'}>
                  <AssignmentLateIcon />
                </Tooltip>
              </SvgIcon>
            ),
          },
          {
            title: t('Department Metrics'),
            path: paths.departmentMetrics,
            icon: (
              <SvgIcon fontSize="medium">
                <Tooltip title={'Department Metrics'}>
                  <BarChartOutlinedIcon />
                </Tooltip>
              </SvgIcon>
            ),
          },
          {
            title: t('MDS Metrics'),
            path: paths.mdsMetrics,
            icon: (
              <SvgIcon fontSize="medium">
                <Tooltip title={'MDS Metrics'}>
                  <QueryStatsOutlinedIcon />
                </Tooltip>
              </SvgIcon>
            ),
          },
          {
            title: t('Report Downloads'),
            path: paths.departmentDownloads,
            icon: (
              <SvgIcon fontSize="medium">
                <Tooltip title={'Report Downloads'}>
                  <FileDownloadOutlinedIcon />
                </Tooltip>
              </SvgIcon>
            ),
          },
          {
            title: t('MDS Dashboard'),
            path: paths.mdsDashboard,
            icon: (
              <SvgIcon fontSize="medium">
                <Tooltip title={'MDS Dashboard'}>
                  <FactCheckOutlinedIcon />
                </Tooltip>
              </SvgIcon>
            ),
          },
        ],
      },
      {
        subheader: t('Actions'),
        items: [
          {
            title: t('Patients'),
            path: paths.patients,
            icon: (
              // <Sv fontSize="medium">
              <PatientIcon />
            ),
          },
          {
            title: t('Daily Notes and Billing'),
            path: paths.dailySessionNotes,
            icon: (
              // <SvgIcon fontSize="medium">
              <DailySessionNotesIcon />
              // </SvgIcon>
            ),
          },
          {
            title: t('Scheduling'),
            path: paths.schedule.index,
            icon: (
              // <SvgIcon fontSize="medium">
              <SchedulingIcon />
              // </SvgIcon>
            ),
            items: [
              {
                title: t('Therapy Planning'),
                path: `${paths.therapyPlanning.index}${paths.therapyPlanning.details}${paths.therapyPlanning.info}`,
              },
              {
                title: t('Therapist Scheduling'),
                path: `${paths.schedule.index}/${paths.schedule.therapistSchedule}`,
              },
            ],
          },
          {
            title: t('Labor Management'),
            path: paths.laborManagement.index,
            icon: (
              // <SvgIcon fontSize="medium">
              <LaborManagementIcon />
              // </SvgIcon>
            ),
            items: [
              {
                title: t('My Availability'),
                path: `${paths.laborManagement.index}/${paths.laborManagement.myAvailability}`,
              },
              {
                title: t('Labor Hour Management'),
                path: `${paths.laborManagement.index}/${paths.laborManagement.laborHoursManagement}`,
              },
              {
                title: t('NPC Log'),
                path: `${paths.laborManagement.index}/${paths.laborManagement.npcLog}`,
              },
              {
                title: t('Labor Report & Approval'),
                path: `${paths.laborManagement.index}/${paths.laborManagement.laborReportAndApproval}`,
              },
              // {
              //   title: t('Labour Hour Management'),
              //   path: `${paths.laborManagement.index}/${paths.laborManagement.laborHoursManagement}`,
              // },
              // {
              //   title: t('Clock in/out'),
              //   path: `${paths.laborManagement.index}/${paths.laborManagement.checkInCheckOut}`,
              // },
              // {
              //   title: t('Labor Report and Approval'),
              //   path: `${paths.laborManagement.index}/${paths.laborManagement.laborReportAndApproval}`,
              // },
            ],
          },
          // {
          //   title: t('Month-End Alerts'),
          //   path: paths.monthDayAlert,
          //   icon: (
          //     <SvgIcon fontSize="medium">
          //       <MonthEndAlertIcon />
          //     </SvgIcon>
          //   ),
          // },
          {
            title: t('Monthly Closeout Alerts'),
            path: paths.monthlyCloseoutAlert,
            icon: (
              // <SvgIcon fontSize="medium">
              <MonthEndAlertIcon />
              // </SvgIcon>
            ),
          },
          {
            title: t('Reports'),
            path: paths.reports,
            icon: (
              // <SvgIcon fontSize="medium">
              <ReportsIcon />
              // </SvgIcon>
            ),
          },
        ],
      },
      // {
      //   subheader: t('RESTORE Integrations'),
      //   items: [
      //     {
      //       title: t('RESTORE Skills'),
      //       path: paths.restoreSkill,
      //       icon: (
      //         <SvgIcon fontSize="medium">
      //           <RSSkillIcon />
      //         </SvgIcon>
      //       ),
      //     },
      //     {
      //       title: t('RESTORE Care'),
      //       path: paths.restoreCare,
      //       icon: (
      //         <SvgIcon fontSize="medium">
      //           <SkillRCIcons />
      //         </SvgIcon>
      //       ),
      //     },
      //     {
      //       title: t('RESTORE Wellness'),
      //       path: paths.restoreWellness,
      //       icon: (
      //         <SvgIcon fontSize="medium">
      //           <RWSkillIcon />
      //         </SvgIcon>
      //       ),
      //     },
      //   ],
      // },
    ];
    const protectedSidebarList: any = sidebarList?.map((list) => {
      if (list?.subheader === '') {
        return {};
      }

      if (
        list?.subheader === 'Dashboards' &&
        (has(Permissions.READ_BOARD_SELF) ||
          has(Permissions.READ_BOARD) ||
          has(Permissions.READ_MDS_DASHBOARD) ||
          has(Permissions.READ_MDS_COORDINATOR_BOARD) ||
          has(Permissions.PHYSICIAN_DASHBOARD))
      ) {
        return {
          subheader: t('Dashboards'),
          items: list?.items?.filter((dashboardsList) => {
            if (dashboardsList?.title === 'My Dashboard' && !has(Permissions.READ_BOARD_SELF))
              return false;
            else if (
              dashboardsList?.title === 'Physician Dashboard' &&
              !has(Permissions.PHYSICIAN_DASHBOARD)
            )
              return false;
            else if (
              (dashboardsList?.title === 'Compliance & Alerts' ||
                dashboardsList?.title === 'Department Metrics' ||
                dashboardsList?.title === 'Report Downloads') &&
              !has(Permissions.READ_BOARD)
            )
              return false;
            else if (
              dashboardsList?.title === 'MDS Dashboard' &&
              (!isMdsDashboardEnabled || !has(Permissions.READ_MDS_DASHBOARD))
            )
              return false;
            // RA-2133: no feature flag — the permission ships disabled for every role by
            // default, so the permission IS the rollout gate.
            else if (
              dashboardsList?.title === 'MDS Metrics' &&
              !has(Permissions.READ_MDS_COORDINATOR_BOARD)
            )
              return false;
            return true;
          }),
        };
      } else if (
        list?.subheader === 'Dashboards' &&
        !(
          has(Permissions.READ_BOARD_SELF) ||
          has(Permissions.READ_BOARD) ||
          has(Permissions.READ_MDS_DASHBOARD) ||
          has(Permissions.READ_MDS_COORDINATOR_BOARD)
        )
      )
        return {
          subheader: t('Dashboards'),
          lists: [],
        };

      if (list?.subheader === 'Actions') {
        const updatedActions: any = [];
        list?.items?.map((action) => {
          if (action?.title === 'Patients' && has(Permissions.READ_PATIENT))
            updatedActions.push(action);
          if (action?.title === 'Daily Notes and Billing' && has(Permissions.READ_DAILY_NOTES))
            updatedActions.push(action);
          if (
            action?.title === 'Scheduling' &&
            (has(Permissions.READ_DAILY_TASK) || has(Permissions.READ_THERAPY_PLANNING))
          )
            updatedActions.push({
              title: t('Scheduling'),
              path: paths.schedule.index,
              icon: (
                // <SvgIcon fontSize="medium">
                <SchedulingIcon />
                // </SvgIcon>
              ),
              items: action?.items?.filter((schedule) => {
                if (
                  schedule?.title === 'Therapy Planning' &&
                  !has(Permissions.READ_THERAPY_PLANNING)
                )
                  return false;
                if (schedule?.title === 'Therapist Scheduling' && !has(Permissions.READ_DAILY_TASK))
                  return false;
                return true;
              }),
            });

          if (
            action?.title === 'Labor Management' &&
            (has(Permissions.READ_AVAILABILITY) || canAccessLaborManagement)
          )
            updatedActions.push({
              title: t('Labor Management'),
              path: paths.laborManagement.index,
              icon: (
                <SvgIcon fontSize="medium">
                  <LaborManagementIcon />
                </SvgIcon>
              ),
              items: action?.items?.filter((schedule) => {
                if (schedule?.title === 'My Availability' && !has(Permissions.READ_AVAILABILITY))
                  return false;
                // if (
                //   schedule?.title === 'Labor Hour Management' &&
                //   (thePayComeDetailsReducer?.enablePaycom ||
                //     thePayComeDetailsReducer?.enableHostedTime)
                // )
                //   return false;
                if (schedule?.title === 'Labor Hour Management' && !canAccessLaborManagement)
                  return false;
                // THIN-8096: gated on the Labor Validation permissions, NOT on labor-management
                // access — the legacy Create/Update Labor Management pair used to be enough to
                // reveal the pay-period approval surface to a therapist who holds neither View
                // nor Edit Labor Validation. Matches the server-side
                // LaborApprovalAccessPolicy.assertCanViewLaborValidation gate on the page's reads.
                if (
                  schedule?.title === 'Labor Report & Approval' &&
                  (!isLaborValidationEnabled || !canViewLaborValidation)
                )
                  return false;
                if (schedule?.title === 'NPC Log' && !isNpcLogEnabled)
                  return false;

                return true;
              }),
            });

          // if (
          //   action?.title === 'Month-End Alerts' &&
          //   has(Permissions.READ_MONTH_END_ALERT)
          // )
          //   updatedActions.push(action);

          if (action?.title === 'Monthly Closeout Alerts' && has(Permissions.READ_MONTH_END_ALERT))
            updatedActions.push(action);
        });
        return {
          subheader: t('Actions'),
          items: updatedActions,
        };
      }

      return list;
    });

    // SQ-60: Append Rearchive link to Actions when feature toggle is on (base URL only).
    const rearchiveUrl = getRearchiveUrl();
    if (isRearchiveNavigationEnabled && rearchiveUrl) {
      return protectedSidebarList.map((section: Section) => {
        if (section?.subheader === t('Actions') && Array.isArray(section?.items)) {
          return {
            ...section,
            items: [
              ...section.items,
              {
                title: 'Rearchive',
                path: rearchiveUrl,
                icon: (
                  <SvgIcon fontSize="medium">
                    <ArchiveOutlinedIcon />
                  </SvgIcon>
                ),
                external: true,
              },
            ],
          };
        }
        return section;
      });
    }

    return protectedSidebarList;
  }, [
    t,
    has,
    isRearchiveNavigationEnabled,
    isMdsDashboardEnabled,
    isNpcLogEnabled,
    isLaborValidationEnabled,
    canAccessLaborManagement,
    canViewLaborValidation,
  ]);
};
